import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SlashCommand, type SlashCommandGroup } from '../../../../lib/slash-command-constants';
interface UseSlashCommandListStateOptions {
  commands: SlashCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: SlashCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
}

/**
 * Shared hook for managing slash command list state including:
 * - Filtering commands based on search query
 * - Grouping commands by category
 * - Keyboard navigation
 * - Expanded command state
 */
export function useSlashCommandListState(t0) {
  const $ = _c(33);
  const {
    commands,
    searchQuery,
    isOpen,
    onSelect,
    onClose,
    inputRef
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedCommandUuid, setExpandedCommandUuid] = useState(null);
  const listRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const itemRefs = useRef(t1);
  let t2;
  bb0: {
    if (!searchQuery || searchQuery === "/") {
      t2 = commands;
      break bb0;
    }
    let t3;
    if ($[1] !== commands || $[2] !== searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase().replace(/^\//, "");
      t3 = commands.filter(command => command.handle.toLowerCase().includes(normalizedQuery) || command.description.toLowerCase().includes(normalizedQuery));
      $[1] = commands;
      $[2] = searchQuery;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = t3;
  }
  const filteredCommands = t2;
  let t3;
  let t4;
  if ($[4] !== filteredCommands) {
    const groups = {};
    filteredCommands.forEach(command_0 => {
      const {
        category
      } = command_0;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(command_0);
    });
    t4 = Object.entries(groups).map(_temp);
    $[4] = filteredCommands;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  t3 = t4;
  const groupedCommands = t3;
  let t5;
  t5 = filteredCommands;
  const flatCommands = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setSelectedIndex(0);
      setExpandedCommandUuid(null);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== filteredCommands.length) {
    t7 = [filteredCommands.length];
    $[7] = filteredCommands.length;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = commandUuid => {
      setExpandedCommandUuid(prev => prev === commandUuid ? null : commandUuid);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const toggleExpanded = t8;
  let t10;
  let t9;
  if ($[10] !== selectedIndex) {
    t9 = () => {
      const selectedItem = itemRefs.current.get(selectedIndex);
      if (selectedItem && listRef.current) {
        selectedItem.scrollIntoView({
          block: "nearest",
          behavior: "smooth"
        });
      }
    };
    t10 = [selectedIndex];
    $[10] = selectedIndex;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  useEffect(t9, t10);
  let t11;
  if ($[13] !== flatCommands || $[14] !== isOpen || $[15] !== onClose || $[16] !== onSelect || $[17] !== selectedIndex) {
    t11 = event => {
      if (!isOpen) {
        return;
      }
      bb44: switch (event.key) {
        case "ArrowDown":
          {
            event.preventDefault();
            setSelectedIndex(prev_1 => (prev_1 + 1) % flatCommands.length);
            break bb44;
          }
        case "ArrowUp":
          {
            event.preventDefault();
            setSelectedIndex(prev_0 => (prev_0 - 1 + flatCommands.length) % flatCommands.length);
            break bb44;
          }
        case "Tab":
          {}
        case "Enter":
          {
            event.preventDefault();
            if (flatCommands[selectedIndex]) {
              onSelect(flatCommands[selectedIndex]);
            }
            break bb44;
          }
        case "Escape":
          {
            event.preventDefault();
            onClose();
            break bb44;
          }
        default:
          {}
      }
    };
    $[13] = flatCommands;
    $[14] = isOpen;
    $[15] = onClose;
    $[16] = onSelect;
    $[17] = selectedIndex;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleKeyDown = t11;
  let t12;
  if ($[19] !== handleKeyDown || $[20] !== inputRef?.current || $[21] !== isOpen) {
    t12 = () => {
      const editor = inputRef?.current;
      if (!editor || !isOpen) {
        return;
      }
      editor.addEventListener("keydown", handleKeyDown as EventListener);
      return () => {
        editor.removeEventListener("keydown", handleKeyDown as EventListener);
      };
    };
    $[19] = handleKeyDown;
    $[20] = inputRef?.current;
    $[21] = isOpen;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== handleKeyDown || $[24] !== inputRef || $[25] !== isOpen) {
    t13 = [inputRef, isOpen, handleKeyDown];
    $[23] = handleKeyDown;
    $[24] = inputRef;
    $[25] = isOpen;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = (index, el) => {
      if (el) {
        itemRefs.current.set(index, el);
      } else {
        itemRefs.current.delete(index);
      }
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const registerItemRef = t14;
  let t15;
  if ($[28] !== expandedCommandUuid || $[29] !== filteredCommands || $[30] !== groupedCommands || $[31] !== selectedIndex) {
    t15 = {
      filteredCommands,
      groupedCommands,
      selectedIndex,
      expandedCommandUuid,
      listRef,
      toggleExpanded,
      registerItemRef
    };
    $[28] = expandedCommandUuid;
    $[29] = filteredCommands;
    $[30] = groupedCommands;
    $[31] = selectedIndex;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}
function _temp(t0) {
  const [category_0, categoryCommands] = t0;
  return {
    category: category_0,
    commands: categoryCommands
  };
}